import React, { useState, useEffect } from 'react';
import { fetchAPI } from '../../utils/api';
import { FaStar, FaTrash, FaUser, FaClock, FaQuoteLeft, FaPlus, FaTimes, FaEdit } from 'react-icons/fa';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { showSuccess, showError } from '../../utils/toastUtils';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from '../../components/common/ConfirmDialog';

export default function AdminFeedback() {
    const { currentUser } = useAuth();
    const { confirm, ConfirmDialog: confirmDialogProps } = useConfirm();
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        author_name: '',
        headline: '',
        content: '',
        rating: 5
    });
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const loadFeedback = async () => {
        try {
            const data = await fetchAPI('/public/get_feedback.php');
            setFeedbacks(data);
        } catch (err) {
            console.error("Failed to load feedback", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFeedback();
    }, []);

    const handleDelete = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Feedback',
            message: 'Are you sure you want to delete this feedback?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            danger: true,
            icon: 'trash'
        });

        if (confirmed) {
            try {
                await fetchAPI('/admin/delete_feedback.php', {
                    method: 'POST',
                    body: JSON.stringify({ id })
                });
                setFeedbacks(feedbacks.filter(f => f.id !== id));
                showSuccess("Feedback deleted successfully!");
            } catch (error) {
                console.error("Failed to delete feedback", error);
                showError("Failed to delete feedback.");
            }
        }
    }

    const handleEdit = (item) => {
        setEditingId(item.id);
        setFormData({
            author_name: item.author,
            headline: item.headline || '',
            content: item.content,
            rating: 5 // Default or calculate from existing if stored
        });
        setShowModal(true);
    }

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingId(null);
        setFormData({ author_name: '', headline: '', content: '', rating: 5 });
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const endpoint = editingId ? '/admin/update_feedback.php' : '/student/save_feedback.php';
            const body = {
                user_id: editingId ? null : (currentUser && !formData.author_name ? currentUser.id : null),
                // Logic: If editing, don't change user_id (handled by backend or ignored). 
                // If adding new: Use currentUser.id ONLY if it's the student themselves submitting (which is not this case),
                // For Admin manual entry, we usually want NULL or the Admin's ID? 
                // Let's pass NULL to be safe and use author_name.
                // Actually, simply pass null for manual entries to avoid FK errors if Admin ID is not in users table (unlikely but possible).
                user_id: null,
                author_name: formData.author_name,
                headline: formData.headline,
                content: formData.content,
                rating: formData.rating
            };

            if (editingId) {
                body.id = editingId;
            }

            await fetchAPI(endpoint, {
                method: 'POST',
                body: JSON.stringify(body)
            });
            handleCloseModal();
            loadFeedback(); // Reload list
            showSuccess(editingId ? "Feedback updated successfully!" : "Feedback added successfully!");
        } catch (error) {
            console.error("Failed to save feedback", error);
            showError("Failed to save feedback. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        Student Feedback
                    </h1>
                    <p className="text-gray-500 font-medium">
                        View and manage testimonials from your students.
                    </p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
                >
                    <FaPlus /> Add Feedback
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-gray-500">Loading feedback...</div>
            ) : feedbacks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No feedback entries found.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {feedbacks.map((item) => (
                        <div key={item.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative group">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold overflow-hidden">
                                        {item.author.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-sm">{item.author}</h3>
                                        <p className="text-xs text-gray-400 flex items-center gap-1">
                                            <FaClock className="text-[10px]" /> {item.time}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex text-yellow-400 text-xs">
                                    {[...Array(5)].map((_, i) => (
                                        <FaStar key={i} />
                                    ))}
                                </div>
                            </div>

                            <FaQuoteLeft className="text-blue-100 text-xl mb-2" />
                            <h4 className="font-bold text-gray-800 text-sm mb-1">{item.headline}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                {item.content}
                            </p>

                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleEdit(item)}
                                    className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                >
                                    <FaEdit />
                                </button>
                                <button
                                    onClick={() => handleDelete(item.id)}
                                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Manual Feedback Modal */}
            <AnimatePresence>
                {showModal && (
                    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                            onClick={handleCloseModal}
                        ></motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="bg-white rounded-[2rem] shadow-2xl relative z-10 w-full max-w-lg my-auto overflow-hidden flex flex-col max-h-[90vh]"
                        >
                            <div className="p-6 md:p-8 border-b border-gray-100 flex justify-between items-center shrink-0 bg-white">
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                                    {editingId ? 'Edit Feedback' : 'Add Feedback Manually'}
                                </h2>
                                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 p-2">
                                    <FaTimes className="text-xl" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden h-full">
                                <div className="p-6 md:p-8 overflow-y-auto space-y-5 custom-scrollbar">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Student Name</label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:border-blue-500 outline-none transition-all font-medium"
                                            placeholder="e.g. John Doe"
                                            value={formData.author_name}
                                            onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Headline</label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:border-blue-500 outline-none transition-all font-medium"
                                            placeholder="Brief summary"
                                            value={formData.headline}
                                            onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Review Content</label>
                                        <textarea
                                            required
                                            rows="4"
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:border-blue-500 outline-none transition-all font-medium"
                                            placeholder="Feedback details..."
                                            value={formData.content}
                                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        ></textarea>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Rating</label>
                                        <div className="flex gap-2 p-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    key={star}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, rating: star })}
                                                    className={`text-3xl ${star <= formData.rating ? 'text-yellow-400' : 'text-gray-200'} hover:scale-110 transition-all`}
                                                >
                                                    <FaStar />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 md:p-8 bg-gray-50 border-t border-gray-100 shrink-0 flex gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="flex-1 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex-[2] py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 disabled:opacity-50"
                                    >
                                        {submitting ? 'Saving...' : (editingId ? 'Update Feedback' : 'Add Feedback')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Confirmation Dialog */}
            {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
        </div>
    );
}
